import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { customersApi } from '../../api/customers.api.js';

type Rating = 'LOW' | 'MEDIUM' | 'HIGH' | 'PROHIBITED';
const RATINGS: readonly Rating[] = ['LOW', 'MEDIUM', 'HIGH', 'PROHIBITED'];

/**
 * Correcting a customer's risk rating by hand.
 *
 * Tracker #41. The scoring engine assigns a rating; a compliance officer who
 * disagrees could not change it without a developer. This sets it, with a
 * required reason — the reason and who gave it are kept on the customer's case
 * file, and the rating is marked manual so a re-score does not put it back.
 */
export function SetRiskRatingButton({
  customerId,
  current,
}: {
  customerId: string;
  current: string;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<Rating>('MEDIUM');
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => customersApi.setRiskRating(customerId, rating, reason.trim()),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['customer', customerId] }),
        queryClient.invalidateQueries({ queryKey: ['customers'] }),
        queryClient.invalidateQueries({ queryKey: ['case-file', customerId] }),
        queryClient.invalidateQueries({ queryKey: ['onboarding-application'] }),
        queryClient.invalidateQueries({ queryKey: ['onboarding-applications'] }),
      ]);
      setOpen(false);
      setReason('');
    },
  });

  return (
    <>
      <Button size="small" variant="text" onClick={() => {
        setRating(RATINGS.includes(current as Rating) ? current as Rating : 'MEDIUM');
        setReason('');
        mutation.reset();
        setOpen(true);
      }}>
        Set rating
      </Button>
      <Dialog open={open} onClose={() => { if (!mutation.isPending) setOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Set risk rating</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              This sets the risk rating for both the onboarding application and the customer
              profile{current !== '' ? ` (currently ${current})` : ''}. Your reason is recorded
              on the case file. Approval and later re-scoring keep this manual rating.
            </Alert>
            <TextField
              select
              label="Rating"
              value={rating}
              disabled={mutation.isPending}
              onChange={(e) => setRating(e.target.value as Rating)}
            >
              {RATINGS.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Reason"
              multiline
              minRows={2}
              value={reason}
              disabled={mutation.isPending}
              onChange={(e) => setReason(e.target.value)}
              error={reason.length > 1000}
              helperText={`${String(reason.length)} / 1,000 characters. Explain your risk assessment.`}
            />
            {mutation.error !== null ? (
              <Alert severity="error">
                {mutation.error instanceof Error ? mutation.error.message : 'Could not set the rating.'}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={mutation.isPending} onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || reason.trim().length < 4 || reason.length > 1000}
          >
            {mutation.isPending ? 'Saving…' : 'Set rating'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
